import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, PageState, TextField, useToast } from '../components/ui';
import { ApiClientError } from '../api/client';
import { fieldErrorsFromApiError } from '../lib/field-errors';
import { useResetPassword } from '../api/auth';

function validate(password: string, confirm: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (password.length < 10) errors.password = 'Password must be at least 10 characters.';
  if (confirm !== password) errors.confirm = 'Passwords do not match.';
  return errors;
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const toast = useToast();
  const resetPassword = useResetPassword();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="ne-auth">
        <Card className="ne-auth__card">
          <PageState
            kind="error"
            title="Invalid link"
            message="This password reset link is missing its token. Request a new one."
          />
          <p className="ne-auth__foot">
            <Link to="/forgot-password">Request a new link</Link>
          </p>
        </Card>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate(password, confirm);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await resetPassword.mutateAsync({ token: token!, password });
      toast.success('Password reset. Please sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'AUTH_TOKEN_INVALID') {
        setError('This link is invalid or has expired. Request a new one.');
      } else if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
        } else {
          setError(err.message);
        }
      } else {
        setError(
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not reset your password. Please try again.',
        );
      }
    }
  }

  return (
    <div className="ne-auth">
      <Card className="ne-auth__card">
        <h1 className="ne-auth__title">Reset password</h1>
        <p className="ne-auth__subtitle">Choose a new password for your account.</p>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFieldErrors((f) => {
                const { password: _p, ...rest } = f;
                return rest;
              });
            }}
            error={fieldErrors.password}
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setFieldErrors((f) => {
                const { confirm: _c, ...rest } = f;
                return rest;
              });
            }}
            error={fieldErrors.confirm}
          />
          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={resetPassword.isPending}>
            {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
          </Button>
        </form>
        <p className="ne-auth__foot">
          <Link to="/login">Back to sign in</Link>
        </p>
      </Card>
    </div>
  );
}
